import React, { useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import {
  useShelterFollowupList, useShelterFollowupSummary, useLoadMoreSentinel,
  useAnimalCoverUrls, fetchFollowupUpdate, AnimalGridSkeleton,
} from '@rehome/shared';
import type { FollowupRow, AnimalFollowupUpdate } from '@rehome/shared';
import {
  CheckCircle2, MapPin, AlertTriangle, CalendarCheck, FilterX, Loader2,
  ShieldCheck, ShieldOff, FileCheck, ChevronRight,
} from 'lucide-react';
import { CheckInDetailDialog } from '../components/CheckInDetailDialog';

// ─── PostAdoptionDashboardPage ─────────────────────────────────────────────
// Menhelyi utókövetés: az örökbefogadott állatok és helyzetjelentéseik.
// "Figyelmeztetés" = egy ütemezett helyzetjelentés (3 nap / 3 hét / 3 hónap)
// lejárt és hiányzik (plan.md 4.3.3).
//
// A lista SZERVEROLDALON összesített, és kurzorosan lapozódik: állatonként
// egy sor jön, a jelentések tartalma nélkül, oldalanként 20 — a következő
// oldal görgetésre tölt be. Az indoklás a 043-as migráció fejlécében.
// ===========================================================================

const CHECKIN_SCHEDULE: Array<{ days: number; type: string; short: string }> = [
  { days: 3, type: '3_Day', short: '3n' },
  { days: 21, type: '3_Week', short: '3h' },
  { days: 90, type: '3_Month', short: '3H' },
];

const getDaysSince = (dateStr?: string | null) => {
  if (!dateStr) return 0;
  return Math.floor((new Date().getTime() - new Date(dateStr).getTime()) / 86400000);
};

const PostAdoptionDashboardPage: React.FC = () => {
  const { shelterId } = useParams<{ shelterId: string }>();
  const [filterWarnings, setFilterWarnings] = useState(false);

  const { rows, loading, loadingMore, hasMore, error, loadMore, reload } =
    useShelterFollowupList(shelterId, filterWarnings);
  const { summary, refetch: refetchSummary } = useShelterFollowupSummary(shelterId);
  const coverUrls = useAnimalCoverUrls(rows);
  const sentinelRef = useLoadMoreSentinel(loadMore, hasMore && !loading);

  // A megnyitott jelentés — a tartalmát csak kattintásra kérjük le.
  const [openUpdate, setOpenUpdate] = useState<AnimalFollowupUpdate | null>(null);
  const [openRow, setOpenRow] = useState<FollowupRow | null>(null);
  const [openingId, setOpeningId] = useState<string | null>(null);

  const openLatestReport = useCallback(async (row: FollowupRow) => {
    if (!row.lastUpdateId) return;
    setOpeningId(row.lastUpdateId);
    const update = await fetchFollowupUpdate(row.lastUpdateId);
    setOpeningId(null);
    if (update) { setOpenRow(row); setOpenUpdate(update); }
  }, []);

  const handleVerified = useCallback(() => {
    reload();
    refetchSummary();
  }, [reload, refetchSummary]);

  const successRate = summary && summary.totalAnimals > 0
    ? Math.round(((summary.totalAnimals - summary.warningAnimals) / summary.totalAnimals) * 100)
    : 100;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Utókövetés</h1>
          <p className="text-gray-600">Örökbefogadott állatok monitorozása</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <StatCard icon={<CheckCircle2 className="w-6 h-6" />} color="green" label="Sikeres Utókövetés" value={summary ? `${successRate}%` : '—'} />
        <StatCard icon={<FileCheck className="w-6 h-6" />} color="blue" label="Beérkezett Jelentések" value={summary ? `${summary.totalReports} db` : '—'} />
        <div
          onClick={() => setFilterWarnings(!filterWarnings)}
          className={`p-6 rounded-xl shadow-sm border flex items-center gap-4 cursor-pointer transition-all group ${filterWarnings ? 'bg-orange-50 border-orange-300 ring-2 ring-orange-200' : 'bg-white border-gray-100 hover:border-orange-200'}`}
        >
          <div className={`p-3 rounded-full transition-colors ${filterWarnings ? 'bg-orange-200 text-orange-700' : 'bg-orange-100 text-orange-600'}`}>
            {filterWarnings ? <FilterX className="w-6 h-6" /> : <AlertTriangle className="w-6 h-6" />}
          </div>
          <div className="flex-1">
            <p className="text-sm text-gray-500 font-medium">Hiányzó helyzetjelentés</p>
            <p className="text-2xl font-bold text-gray-900">{summary ? `${summary.warningAnimals} eset` : '—'}</p>
            <p className="text-xs text-orange-600 mt-1 font-medium group-hover:underline">{filterWarnings ? 'Szűrés törlése' : 'Lista szűrése'}</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-6 border-b border-gray-100 flex justify-between items-center">
          <h2 className="text-lg font-bold text-gray-800">{filterWarnings ? 'Hiányzó helyzetjelentések' : 'Aktív Utókövetések'}</h2>
          {filterWarnings && (
            <span className="text-xs font-medium bg-orange-100 text-orange-800 px-3 py-1 rounded-full flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> Szűrés aktív
            </span>
          )}
        </div>

        {loading && <div className="p-6"><AnimalGridSkeleton count={3} /></div>}

        {!loading && error && (
          <div className="px-6 py-10 text-center">
            <p className="text-gray-500 mb-3">A lista betöltése nem sikerült.</p>
            <button onClick={reload} className="text-sm font-semibold text-teal-700 hover:underline">Újrapróbálom</button>
          </div>
        )}

        {!loading && !error && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="px-6 py-4">Állat &amp; Gazdi</th>
                  <th className="px-6 py-4">Örökbefogadva</th>
                  <th className="px-6 py-4">Automatizáció</th>
                  <th className="px-6 py-4">Utolsó Jelentés</th>
                  <th className="px-6 py-4">Státusz</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.length === 0 && (
                  <tr><td colSpan={5} className="px-6 py-10 text-center text-gray-400">
                    {filterWarnings ? 'Nincs hiányzó check-in. Szép munka!' : 'Még nincs örökbefogadott állat ennél a menhelynél.'}
                  </td></tr>
                )}

                {rows.map(row => {
                  const days = getDaysSince(row.adoptionDate);
                  const doneTypes = new Set(row.doneTypes || []);
                  const isWarning = (row.missingTypes || []).length > 0;
                  const canOpen = Boolean(row.lastUpdateId);

                  return (
                    <tr key={row.id} className={`hover:bg-gray-50 ${isWarning && filterWarnings ? 'bg-orange-50/30' : ''}`}>
                      <td className="px-6 py-4">
                        <Link
                          to={`/shelter/${shelterId}/animals/${row.id}`}
                          className="flex items-center gap-3 group/animal"
                        >
                          {coverUrls[row.id] ? (
                            <img src={coverUrls[row.id]} className="w-10 h-10 rounded-full object-cover" alt="" />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-gray-100" />
                          )}
                          <div>
                            <div className="font-bold text-gray-900 group-hover/animal:text-teal-700 transition-colors">{row.name}</div>
                            <div className="text-xs text-gray-500">{row.adopterName || 'Ismeretlen'}</div>
                          </div>
                        </Link>
                      </td>

                      <td className="px-6 py-4">
                        <div>{row.adoptionDate || '-'}</div>
                        <div className="text-xs text-gray-400">{days} napja</div>
                      </td>

                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          {CHECKIN_SCHEDULE.map(s => {
                            const done = doneTypes.has(s.type);
                            const overdue = days > s.days && !done;
                            return (
                              <span key={s.type} title={done ? 'Beérkezett' : overdue ? 'Lejárt, hiányzik' : 'Még nem esedékes'}
                                className={`w-8 h-8 rounded-full flex items-center justify-center border text-xs font-bold ${
                                  done ? 'bg-green-100 text-green-700 border-green-200'
                                    : overdue ? 'bg-orange-100 text-orange-700 border-orange-300'
                                    : 'bg-gray-100 text-gray-400 border-gray-200'
                                }`}>{s.short}</span>
                            );
                          })}
                        </div>
                      </td>

                      <td className="px-6 py-4">
                        {canOpen ? (
                          // Kattintásra nyílik a részletes dialog — eddig a
                          // beküldött jelentést egyáltalán nem lehetett megnyitni.
                          <button
                            type="button"
                            onClick={() => openLatestReport(row)}
                            disabled={openingId === row.lastUpdateId}
                            className="text-left group/report disabled:opacity-60"
                          >
                            <div className="flex items-center gap-1 text-xs font-medium text-gray-700 group-hover/report:text-teal-700 transition-colors">
                              {openingId === row.lastUpdateId
                                ? <Loader2 className="w-3 h-3 animate-spin" />
                                : <CalendarCheck className="w-3 h-3" />}
                              {(row.lastUpdateAt || '').split('T')[0]}
                              <ChevronRight className="w-3 h-3 opacity-0 group-hover/report:opacity-100 transition-opacity" />
                            </div>
                            <div className="text-[11px] text-gray-400 group-hover/report:text-teal-600 transition-colors">
                              {row.reportCount} jelentés · részletek
                            </div>
                            {row.lastUpdateHasGps && (
                              <div className="flex items-center gap-1 text-xs text-green-600 mt-1"><MapPin className="w-3 h-3" /> GPS OK</div>
                            )}
                          </button>
                        ) : (
                          <span className="text-gray-400 italic">Még nincs jelentés</span>
                        )}
                      </td>

                      <td className="px-6 py-4">
                        {row.lastUpdateId ? (
                          row.lastUpdateVerified ? (
                            <span className="text-xs font-bold border px-3 py-1 rounded-full bg-green-50 text-green-700 border-green-200 inline-flex items-center gap-1">
                              <ShieldCheck className="w-3 h-3" /> Visszaigazolva
                            </span>
                          ) : (
                            <button
                              onClick={() => openLatestReport(row)}
                              className="text-xs font-bold border px-3 py-1 rounded-full bg-yellow-50 text-yellow-700 border-yellow-200 hover:bg-yellow-100 transition-colors inline-flex items-center gap-1"
                            >
                              <ShieldOff className="w-3 h-3" /> Megerősítés
                            </button>
                          )
                        ) : (
                          <span className="text-xs text-gray-400 italic">Nincs frissítés</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Görgetésre töltő őrszem. A magassága nem nulla, különben a
                megfigyelő sosem jelezne átlépést. */}
            {hasMore && (
              <div ref={sentinelRef} className="py-6 flex items-center justify-center text-sm text-gray-400 gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> További állatok betöltése…
              </div>
            )}
            {!hasMore && rows.length > 0 && (
              <div className="py-4 text-center text-xs text-gray-400">
                A lista végére értél — {rows.length} állat.
              </div>
            )}
            {loadingMore && !hasMore && (
              <div className="py-4 flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-gray-400" /></div>
            )}
          </div>
        )}
      </div>

      <CheckInDetailDialog
        update={openUpdate}
        animalName={openRow?.name || ''}
        adopterName={openRow?.adopterName}
        onClose={() => { setOpenUpdate(null); setOpenRow(null); }}
        onVerified={handleVerified}
      />
    </div>
  );
};

const STAT_COLOR_STYLES: Record<string, string> = {
  green: 'bg-green-100 text-green-600',
  blue: 'bg-blue-100 text-blue-600',
  orange: 'bg-orange-100 text-orange-600',
};

const StatCard: React.FC<{ icon: React.ReactNode; color: string; label: string; value: string }> = ({ icon, color, label, value }) => (
  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center gap-4">
    <div className={`p-3 ${STAT_COLOR_STYLES[color] ?? STAT_COLOR_STYLES.green} rounded-full`}>{icon}</div>
    <div><p className="text-sm text-gray-500">{label}</p><p className="text-2xl font-bold text-gray-900">{value}</p></div>
  </div>
);

export default PostAdoptionDashboardPage;
